import { computed, action, observable } from 'mobx';
import { Breakpoints } from '@estee/elc-base-theme';
import { ViewController } from '@estee/elc-service';
import { emit, events, on, query, serviceQueries } from '@estee/elc-service-bus';
import { root } from '@estee/elc-universal-utils';
import * as VtoConstants from '~constants/Vto';
import * as VtoSkinCareConstants from '~constants/VtoSkinCare';

import {
    IVtoConfig,
    IProduct,
    IVtoEventListenerResult,
    IVtoFoundationShadeLabels,
    IVtoFoundationPerfectMatches,
    ISku
} from '~interfaces/IVto';
import { IVtoSkinCareHandlerResult, IVtoSkinConcernScores } from '~interfaces/IVtoSkinCare';
import { IFeature } from '~constants/Features';
import { isIOS, isSafari, isUserMediaEnabled } from '~utils/UserAgent';

export interface IVtoApiMethods {
    init({}): void;
    open(liveCam: boolean, image: object | null, detectSkin: boolean): void;
    closeEngine(): void;
    close(): void;
    applyMakeupBySku(skuId: string): void;
    addEventListener(
        event: string,
        callback: (result: IVtoEventListenerResult | IVtoSkinConcernScores) => void
    ): number;
    removeEventListener(listenerId: number): void;
    openSkincare(skincareMode: string): void;
    getSkincareRecommendationList({}): void;
}

export interface IVtoWindow extends Window {
    ymkAsyncInit(): void;
    YMK: IVtoApiMethods;
}

type VtoEventsType = typeof VtoConstants.VtoEvents | typeof VtoSkinCareConstants.VtoSkinCareEvents;
interface ICallbackHandlers {
    [key: string]: (result: IVtoEventListenerResult | IVtoSkinCareHandlerResult) => void;
}

declare let window: IVtoWindow;

export class VtoBaseController extends ViewController {
    @observable public product: IProduct;
    @observable public isVtoOpened: boolean = false;
    @observable public isSupportedBrowserOverlayVisible: boolean = false;
    @observable public isSupportedBrowserOverlayOpen: boolean = true;
    public isVtoFoundationEnabled: boolean;
    public isVtoMakeupEnabled: boolean;
    public vtoFoundationPerfectMatchText: IVtoFoundationShadeLabels;
    public handleShadeSelect: (name: string, eventLabel: string) => void;
    public bodyElementClass: DOMTokenList = document.body.classList;
    public eventListenerIdentifiers: number[] = [];
    public iframeWidth: number;
    public iframeHeight: number;
    public vtoConfig: IVtoConfig;
    public isVtoDisclosed: boolean = false;
    public isVtoPriceHidden: boolean = false;
    public isVtoFoundationMultiIds: boolean;
    public hasShadeSidebarVto: boolean = false;
    public productIdList: string[];
    private previousWindowHeight: number;
    static isFoundationQrCodeOverlayOpen = false;

    @computed
    get vtoFeature() {
        return this.vtoConfig && this.vtoConfig.vtoFeature;
    }

    @computed
    get vtoFoundationFeature() {
        return this.vtoFeature && this.vtoFeature.foundation;
    }

    @computed
    get vtoMakeupFeature() {
        return this.vtoFeature && this.vtoFeature.makeup;
    }

    @action
    public setIsOpenedState = (isOpened: boolean) => {
        this.isVtoOpened = isOpened;
    };

    @computed
    public get getVtoApiConfig() {
        if (!this.vtoConfig || !this.vtoConfig.features) {
            return;
        }

        if (this.isVtoFoundationEnabled) {
            return this.vtoConfig.features.vtoFoundation;
        }

        return this.vtoConfig.features.vtoMakeup;
    }

    public loadVto = (apiUrl: string, apiKey: string) => {
        if (!!document.querySelector(`script[src="${apiUrl}${apiKey}"]`)) {
            return;
        }
        const script = document.getElementsByTagName('script')[0];
        const vtoScript = document.createElement('script');
        vtoScript.type = 'text/javascript';
        vtoScript.async = true;
        vtoScript.src = `${apiUrl}${apiKey}`;
        if (script !== undefined && script.parentNode !== null) {
            script.parentNode.insertBefore(vtoScript, script);
        }
    };

    public getIframeBgColor = () => {
        const vtoMakeupIframeColor = this.vtoConfig.vtoFeature?.makeup?.iframeColor;
        if (this.vtoConfig.vtoFeature?.makeup?.makeupVariant && vtoMakeupIframeColor) {
            return vtoMakeupIframeColor;
        }
    };

    public isFourWayLooksEnabled = () => {
        const hasFourWayLooks = this.vtoConfig.vtoFeature?.makeup?.hasFourWayLooks;
        const sppContainer = document.getElementsByClassName('js-spp-content');
        const isVtoMakeupFourWayLooksEnabled = sppContainer[0]?.getAttribute(
            'data-enable-four-way-looks'
        );
        const isMakeupVariant = this.vtoConfig.vtoFeature?.makeup?.makeupVariant;

        return isMakeupVariant && hasFourWayLooks && isVtoMakeupFourWayLooksEnabled === 'true';
    };

    public getIframeDimension = () => {
        const imageContainer = this.getImageContainer();
        const imageContainerWidth = (imageContainer as HTMLElement).offsetWidth;

        // Reduce extra padding for larger screens
        const imageContainerModifiedWidth =
            window.innerWidth >= Breakpoints.desktop
                ? imageContainerWidth - VtoConstants.IFRAME_EXTRA_PADDING
                : imageContainerWidth;

        let iframeWidth = imageContainerModifiedWidth;

        // Set max width if the container exceeds max width
        if (imageContainerModifiedWidth > VtoConstants.IFRAME_MAX_WIDTH) {
            iframeWidth = VtoConstants.IFRAME_MAX_WIDTH;
        }

        // Set min width if the container width is lesser than the min width
        if (imageContainerModifiedWidth < VtoConstants.IFRAME_MIN_WIDTH) {
            iframeWidth = VtoConstants.IFRAME_MIN_WIDTH;
        }

        // Calculate height using VTO ratio
        let iframeHeight = iframeWidth * VtoConstants.IFRAME_RATIO;

        if (this.hasShadeSidebarVto) {
            [iframeWidth, iframeHeight] = this.getShadeSidebarVtoDimension(
                iframeWidth,
                iframeHeight
            );
        }

        return {
            iframeWidth,
            iframeHeight
        };
    };

    private getImageContainer() {
        const desktopSelectorClass = this.hasShadeSidebarVto
            ? VtoConstants.SHADE_SIDEBAR_CONTAINER
            : VtoConstants.LEFT_STICKY_COLUMN;

        const desktopSelector =
            document.querySelector(`.${desktopSelectorClass}`) ||
            document.querySelector(`.${VtoConstants.LEFT_COLUMN_SECTION}`);

        if (window.innerWidth >= Breakpoints.desktop) {
            return desktopSelector;
        }

        return document.querySelector(`.${VtoConstants.PRODUCT_FULL_SECTION}`);
    }

    private getShadeSidebarVtoDimension(iframeWidth: number, iframeHeight: number) {
        const {
            SHADE_SIDEBAR_CONTAINER,
            IFRAME_MAX_WIDTH,
            IFRAME_RATIO,
            SHADE_SIDEBAR_HEIGHT_RATIO,
            IFRAME_MIN_WIDTH
        } = VtoConstants;

        const shadeSidebarInfo = document.querySelector(`.${SHADE_SIDEBAR_CONTAINER}`);

        if (!shadeSidebarInfo) {
            return [iframeWidth, iframeHeight];
        }

        const shadeSidebarInfoWidth = (shadeSidebarInfo as HTMLElement).offsetWidth;
        const iframeMaxHeight = IFRAME_MAX_WIDTH * IFRAME_RATIO;
        const heightDifference = this.previousWindowHeight - window.innerHeight;
        const [downloadBarMin, downloadBarMax] = this.downloadBarVariation;

        // Calculate shade sidebar overlay height using its ratio
        let shadeSidebarHeight = window.innerHeight * SHADE_SIDEBAR_HEIGHT_RATIO;

        // Don't change the height when the download bar appears at the bottom of Chrome browser
        if (heightDifference >= downloadBarMin && heightDifference <= downloadBarMax) {
            shadeSidebarHeight = this.previousWindowHeight * SHADE_SIDEBAR_HEIGHT_RATIO;
        }

        this.previousWindowHeight = window.innerHeight;

        // Sometimes, mainly in iPad, shadeSidebarHeight might be larger than iframe's max height.
        // In this case, iframeMaxHeight should be taken instead of shadeSidebarHeight
        let shadeSidebarIframeHeight =
            shadeSidebarHeight < iframeMaxHeight ? shadeSidebarHeight : iframeMaxHeight;

        // Since the overlay is fixed and non-scrollable, we should give precedence to height over width and
        // so width is being calculated based on height
        let shadeSidebarIframeWidth = shadeSidebarIframeHeight / IFRAME_RATIO;

        // The above calculated width should not exceed the shadeSidebarInfo width and should not be
        // lesser than min width
        if (shadeSidebarIframeWidth > shadeSidebarInfoWidth) {
            shadeSidebarIframeWidth = shadeSidebarInfoWidth;
            shadeSidebarIframeHeight = shadeSidebarIframeWidth * IFRAME_RATIO;
        }

        if (shadeSidebarIframeWidth < IFRAME_MIN_WIDTH) {
            shadeSidebarIframeWidth = IFRAME_MIN_WIDTH;
            shadeSidebarIframeHeight = shadeSidebarIframeWidth * IFRAME_RATIO;
        }

        return [shadeSidebarIframeWidth, shadeSidebarIframeHeight];
    }

    private get downloadBarVariation() {
        const { DOWNLOAD_BAR_SIZE, DOWNLOAD_BAR_TOLERANCE, DOWNLOAD_BAR_VARIATION } = VtoConstants;

        const downloadBarMinSize =
            DOWNLOAD_BAR_SIZE * (DOWNLOAD_BAR_VARIATION - DOWNLOAD_BAR_TOLERANCE);
        const downloadBarMaxSize =
            DOWNLOAD_BAR_SIZE * (DOWNLOAD_BAR_VARIATION + DOWNLOAD_BAR_TOLERANCE);

        return [downloadBarMinSize, downloadBarMaxSize];
    }

    public handleVtoResize = (reset: boolean) => {
        const hasLipVto = this.vtoFoundationFeature && this.vtoFoundationFeature.hasLipVto;
        if (hasLipVto) {
            const iframe = document.getElementById('YMK-module-iframe');
            const maxHeight = window.innerHeight;
            const compareOverlayOffset =
                this.vtoFoundationFeature && this.vtoFoundationFeature.compareOverlayOffset;

            if (iframe && compareOverlayOffset) {
                iframe.style.height = reset
                    ? `${maxHeight}px`
                    : `${maxHeight - compareOverlayOffset}px`;
            }
        }
    };

    public getVtoParams = () => {
        const compareOverlayOffset =
            this.vtoFoundationFeature && this.vtoFoundationFeature.compareOverlayOffset;
        const bottomPanelOffsetY = compareOverlayOffset || VtoConstants.COMPARE_BUTTON_HEIGHT;
        const isDesktop = window.innerWidth >= Breakpoints.desktop;
        const { iframeWidth, iframeHeight } = this.getIframeDimension();
        const { language } = <IFeature>this.getVtoApiConfig;
        const sppContainer = document.getElementsByClassName('js-spp-content');
        const iframeColor = sppContainer[0]?.getAttribute('data-style-color');
        const styleColor = !!iframeColor && this.getIframeBgColor() ? this.getIframeBgColor() : '';
        const enableFourWay = this.isFourWayLooksEnabled();
        const enableFBSharing = this.vtoConfig.vtoFeature?.makeup?.hasFBSharing;
        const enableTwitterSharing = this.vtoConfig.vtoFeature?.makeup?.hasTwitterSharing;
        const enablePinterestSharing = this.vtoConfig.vtoFeature?.makeup?.hasPinterestSharing;
        const skinSmoothStrength = this?.vtoFoundationFeature?.skinSmoothStrength;
        this.iframeWidth = iframeWidth;
        this.iframeHeight = iframeHeight;

        if (this.isVtoFoundationEnabled) {
            return {
                autoOpen: false,
                showCloseBtnOnLauncher: true,
                bottomPanelOffsetY: bottomPanelOffsetY,
                ...(this.isVtoFoundationMultiIds && {
                    productIdList: this.productIdList
                }),
                ...(language && { language }),
                ...(typeof skinSmoothStrength === 'number' && skinSmoothStrength >= 0 && { skinSmoothStrength: Math.max(0, Math.min(skinSmoothStrength, 100)) })
            };
        }

        return {
            autoOpen: true,
            skinSmoothStrength: VtoConstants.SKIN_SMOOTH_STRENGTH,
            showCloseBtnOnLauncher: !this.isVtoDisclosed,
            width: isDesktop ? iframeWidth : '',
            height: isDesktop ? iframeHeight : '',
            styleColor: styleColor,
            ...(enableFourWay && {
                enableFourWayLooks: enableFourWay
            }),
            ...(!enableFBSharing && { enableFBSharing }),
            ...(!enableTwitterSharing && { enableTwitterSharing }),
            ...(!enablePinterestSharing && { enablePinterestSharing }),
            ...(language && { language })
        };
    };

    public filterPerfectMatches = (perfectMatchResults: IVtoFoundationPerfectMatches) => {
        const vtoFoundationShadeValues = Object.values(VtoConstants.FoundationShadeTypes);

        return Object.entries(perfectMatchResults).reduce(
            (perfectMatchAccumulator, [label, skuId]) => {
                // VTO sends us the best shade in the name of "guid"
                const shadeLabel =
                    label === VtoConstants.VTO_GUID
                        ? VtoConstants.FoundationShadeTypes.BEST
                        : (label as VtoConstants.FoundationShadeTypes);
                if (vtoFoundationShadeValues.indexOf(shadeLabel) > -1) {
                    perfectMatchAccumulator[skuId] = this.vtoFoundationPerfectMatchText[shadeLabel];
                }

                return perfectMatchAccumulator;
            },
            {} as IVtoFoundationPerfectMatches
        );
    };

    public updateShadeEntity = (perfectMatches: IVtoFoundationPerfectMatches) => {
        const perfectMatchSkuIds = Object.keys(perfectMatches);
        this.product.shades.forEach((shade) => {
            if (shade.parentSku && perfectMatchSkuIds.indexOf(shade.parentSku.skuId) > -1) {
                const perfectShadeLabel = perfectMatches[shade.parentSku.skuId];
                shade.changeToPerfect && shade.changeToPerfect(perfectShadeLabel);
            } else {
                shade.resetPerfect && shade.resetPerfect();
                shade.showNameInsideShadeCircle && shade.showNameInsideShadeCircle();
            }
        });
    };

    public selectPerfectSku = async (result?: IVtoFoundationPerfectMatches) => {
        // VTO sends us the best shade in the name of "guid"
        const skuId = result && result[VtoConstants.VTO_GUID];

        if (skuId) {
            const productData = <IProduct[]>await query(serviceQueries.GET_PRODUCT_DATA, {
                filterBy: { skus: { skuIds: [skuId] } }
            });

            const skuData = productData[0].skus.find((sku) => sku.skuId === skuId);

            const { shades } = <ISku>skuData;
            const eventLabel = `${productData[0].productId} - ${productData[0].skus[0].perlgem.SKU_BASE_ID} - ${productData[0].displayName}`;

            if (this.handleShadeSelect) {
                this.handleShadeSelect(shades[0].name, eventLabel);
            }
        }
    };

    public selectFirstSku = async (skuId?: string) => {
        if (skuId) {
            const productData = <IProduct[]>await query(serviceQueries.GET_PRODUCT_DATA, {
                filterBy: { skus: { skuIds: [skuId] } }
            });

            const skuData = productData[0].skus.find((sku) => sku.skuId === skuId);

            const { shades } = <ISku>skuData;
            const eventLabel = `${productData[0].productId} - ${productData[0].skus[0].perlgem.SKU_BASE_ID} - ${productData[0].displayName}`;

            if (this.handleShadeSelect) {
                this.handleShadeSelect(shades[0].name, eventLabel);
            }
        }
    };

    public resetShadeProps = () => {
        if (this.isVtoMakeupEnabled) {
            return;
        }

        this.product.shades.forEach((shade) => shade.resetPerfect && shade.resetPerfect());
    };

    public addEventListeners = (vtoEvents: VtoEventsType, callback: ICallbackHandlers) => {
        if (typeof window.YMK !== 'object' || this.eventListenerIdentifiers.length) {
            return;
        }
        Object.values(vtoEvents).forEach((event) => {
            const eventListenerIdentifier = window.YMK.addEventListener(event, (result) =>
                callback[event](result as IVtoEventListenerResult)
            );
            this.eventListenerIdentifiers.push(eventListenerIdentifier);
        });
    };

    public removeVtoEventListeners = () => {
        if (!this.eventListenerIdentifiers.length) {
            return;
        }

        this.eventListenerIdentifiers.forEach((eventListenerIdentifier) =>
            window.YMK.removeEventListener(eventListenerIdentifier)
        );
        this.eventListenerIdentifiers = [];
    };

    public toggleBodyClasses = (hasBodyClasses: boolean) => {
        const isMobile = window.innerWidth < Breakpoints.desktop;
        const bodyClasses = [VtoConstants.NO_SCROLL_CLASS, VtoConstants.VTO_ACTIVE_CLASS];
        isMobile && hasBodyClasses
            ? this.bodyElementClass.add(...bodyClasses)
            : this.bodyElementClass.remove(...bodyClasses);
    };

    public checkVtoDisclose = () => {
        const vtoDiscloseParam = this.getQueryParam(VtoConstants.VTO_DISCLOSE_PARAM);
        this.isVtoDisclosed = vtoDiscloseParam !== null;
    };

    public checkVtoHidePriceParam = () => {
        const vtoHidePriceParam = this.getQueryParam(VtoConstants.VTO_HIDE_PRICE_PARAM);
        this.isVtoPriceHidden = vtoHidePriceParam !== null;
    };

    public getQueryParam = (searchKey: string) => {
        const search = (<Location>root.location).search;
        const urlParams = new URLSearchParams(search);

        return urlParams.get(searchKey);
    };

    get isSupportedBrowserUserAgent() {
        return isIOS() && !isSafari() && !isUserMediaEnabled();
    }

    public showSupportedBrowserOverlay = () => {
        this.setIsSupportedBrowserOverlayVisible(true);
        this.openSupportedBrowserOverlay();
    };

    public closeSupportedBrowserOverlay = (vtoType: VtoConstants.VtoTypes) => {
        this.setIsSupportedBrowserOverlayOpen(false);
        emit(events.VTO_CLOSED, { vtoType });
    };

    private openSupportedBrowserOverlay = () => {
        this.setIsSupportedBrowserOverlayOpen(true);
    };

    @action
    public setIsSupportedBrowserOverlayVisible = (isEnabled: boolean) => {
        this.isSupportedBrowserOverlayVisible = isEnabled;
    };

    @action
    public setIsSupportedBrowserOverlayOpen = (isOpened: boolean) => {
        this.isSupportedBrowserOverlayOpen = isOpened;
    };

    @on(events.VTO_OPENED)
    public openVTO = () => {
        this.setIsOpenedState(true);
    };

    @on(events.VTO_CLOSED)
    public closeVTO = () => {
        this.setIsOpenedState(false);
    };
}
